import { ArrowLeft, Save } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import FieldLabel from "../components/FieldLabel";
import RelationshipPropertyRow, {
  RelationshipRowEntry,
} from "../components/RelationshipPropertyRow";
import TagField from "../components/TagField";
import TypeaheadUserInput from "../components/TypeaheadUserInput";
import { CRA_CLASSIFICATION_OPTIONS } from "../constants/craClassification";
import { productFieldDescriptions } from "../constants/productFieldDescriptions";
import { useAuth } from "../hooks/useAuth";
import { apiService } from "../services/api";
import {
  hasGlobalAccess,
  ProductCreate,
  ProductTagInput,
  RelationshipInput,
  RelationshipTypeDefinition,
  TagDefinitionCategory,
} from "../types";

export default function CreateProductPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [formData, setFormData] = useState<ProductCreate>({
    name: "",
    businessArea: "",
    division: "",
    description: "",
    category: "",
  });
  const [selectedTags, setSelectedTags] = useState<ProductTagInput[]>([]);
  const [relationships, setRelationships] = useState<
    (RelationshipInput & { _targetName?: string })[]
  >([]);

  // Fetch business areas
  const { data: businessAreasResponse, isLoading: loadingBusinessAreas } =
    useQuery("businessAreas", () => apiService.getBusinessAreas(), {
      select: (response) =>
        response.data.success ? (response.data.data as string[]) : [],
    });

  // Set initial business area filter for non-global users
  useEffect(() => {
    if (
      user &&
      !hasGlobalAccess(user) &&
      user.businessArea &&
      !formData.businessArea
    ) {
      setFormData((prev) => ({
        ...prev,
        businessArea: user.businessArea || "",
      }));
    }
  }, [user, formData.businessArea]);

  // Filter business areas based on user permissions
  const availableBusinessAreas =
    hasGlobalAccess(user) || !user?.businessArea
      ? businessAreasResponse || []
      : user?.businessArea
        ? [user.businessArea]
        : [];

  // Fetch divisions when business area is selected
  const { data: divisionsResponse, isLoading: loadingDivisions } = useQuery(
    ["divisions", formData.businessArea],
    () => apiService.getDivisions(formData.businessArea),
    {
      enabled: !!formData.businessArea,
      select: (response) =>
        response.data.success ? (response.data.data as string[]) : [],
    },
  );

  // Fetch users for responsibility assignments
  const { data: usersResponse, isLoading: loadingUsers } = useQuery(
    "users",
    () => apiService.getUsers(),
    {
      select: (response) =>
        response.data.success ? (response.data.data as any[]) : [],
    },
  );

  // Fetch tag definitions
  const { data: tagDefinitions } = useQuery(
    "tagDefinitions",
    () => apiService.getTagDefinitions(),
    {
      select: (response) =>
        response.data.success
          ? (response.data.data as TagDefinitionCategory[])
          : [],
    },
  );

  // Fetch relationship type definitions
  const { data: relationshipTypes } = useQuery(
    "relationshipTypes",
    () => apiService.getRelationshipTypes(),
    {
      select: (response) =>
        response.data.success
          ? (response.data.data as RelationshipTypeDefinition[])
          : [],
    },
  );

  // Reset division when business area changes
  useEffect(() => {
    if (formData.businessArea && formData.division) {
      setFormData((prev) => ({ ...prev, division: "" }));
    }
  }, [formData.businessArea]);

  const createProductMutation = useMutation(
    (data: ProductCreate) => apiService.createProduct(data),
    {
      onSuccess: (response) => {
        if (response.data.success) {
          queryClient.invalidateQueries(["products"]);
          toast.success("Product created successfully!");
          navigate("/products");
        } else {
          toast.error(response.data.error || "Failed to create product");
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || "Failed to create product");
      },
    },
  );

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Special handlers for responsibility fields
  const handleProductResponsibleChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      productResponsibleId: value,
    }));
  };

  const handleBusinessAccountableChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      businessAccountableId: value,
    }));
  };

  const handleSdlResponsibleChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      sdlResponsibleId: value,
    }));
  };

  const handleTagChange = (keyId: string, values: string[]) => {
    setSelectedTags((prev) => [
      ...prev.filter((t) => t.key !== keyId),
      ...values.map((value) => ({ key: keyId, value })),
    ]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.businessArea || !formData.division) {
      toast.error("Please fill in all required fields");
      return;
    }

    createProductMutation.mutate({
      ...formData,
      tags: selectedTags.length > 0 ? selectedTags : undefined,
      relationships:
        relationships.length > 0
          ? relationships.map(({ _targetName, ...r }) => r)
          : undefined,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/products")}
            className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Create Product
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              Add a new product to the system
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Product Name */}
              <div className="md:col-span-2">
                <FieldLabel
                  htmlFor="name"
                  description={productFieldDescriptions.name}
                >
                  Product Name *
                </FieldLabel>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="input"
                  placeholder="Enter product name"
                />
              </div>

              {/* Business Area */}
              <div>
                <FieldLabel
                  htmlFor="businessArea"
                  description={productFieldDescriptions.businessArea}
                >
                  Business Area *
                </FieldLabel>
                <select
                  id="businessArea"
                  name="businessArea"
                  value={formData.businessArea}
                  onChange={handleInputChange}
                  required
                  className="input"
                  disabled={loadingBusinessAreas}
                >
                  <option value="">Select a business area</option>
                  {availableBusinessAreas?.map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  ))}
                </select>
              </div>

              {/* Division */}
              <div>
                <FieldLabel
                  htmlFor="division"
                  description={productFieldDescriptions.division}
                >
                  Division/Business Unit *
                </FieldLabel>
                <select
                  id="division"
                  name="division"
                  value={formData.division}
                  onChange={handleInputChange}
                  required
                  className="input"
                  disabled={!formData.businessArea || loadingDivisions}
                >
                  <option value="">
                    {!formData.businessArea
                      ? "Select a business area first"
                      : loadingDivisions
                        ? "Loading divisions/business units..."
                        : "Select a division/business unit"}
                  </option>
                  {divisionsResponse?.map((division) => (
                    <option key={division} value={division}>
                      {division}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category */}
              <div>
                <FieldLabel
                  htmlFor="category"
                  description={productFieldDescriptions.category}
                >
                  Category
                </FieldLabel>
                <input
                  type="text"
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  maxLength={20}
                  className="input"
                  placeholder="e.g., Web Application, Mobile App"
                />
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <FieldLabel
                  htmlFor="description"
                  description={productFieldDescriptions.description}
                >
                  Description
                </FieldLabel>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  className="input"
                  placeholder="Describe the product and its purpose..."
                />
              </div>
            </div>
          </div>

          {/* Product Details Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Product Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* APM Number */}
              <div>
                <FieldLabel
                  htmlFor="apmNumber"
                  description={productFieldDescriptions.apmNumber}
                >
                  APM Number
                </FieldLabel>
                <input
                  type="text"
                  id="apmNumber"
                  name="apmNumber"
                  value={formData.apmNumber || ""}
                  onChange={handleInputChange}
                  maxLength={30}
                  className="input"
                  placeholder="Enter APM number"
                />
              </div>

              {/* Development Responsibility */}
              <div>
                <FieldLabel
                  htmlFor="developmentResponsibility"
                  description={
                    productFieldDescriptions.developmentResponsibility
                  }
                >
                  Development Responsibility
                </FieldLabel>
                <select
                  id="developmentResponsibility"
                  name="developmentResponsibility"
                  value={formData.developmentResponsibility || ""}
                  onChange={handleInputChange}
                  className="input"
                >
                  <option value="">Select development responsibility</option>
                  <option value="Sandvik">Sandvik</option>
                  <option value="Supplier">Supplier</option>
                  <option value="Sub-Contractor">Sub-Contractor</option>
                </select>
              </div>

              {/* Customization */}
              <div>
                <FieldLabel
                  htmlFor="customization"
                  description={productFieldDescriptions.customization}
                >
                  Customization
                </FieldLabel>
                <select
                  id="customization"
                  name="customization"
                  value={formData.customization || ""}
                  onChange={handleInputChange}
                  className="input"
                >
                  <option value="">Select customization type</option>
                  <option value="Customized">Customized</option>
                  <option value="COTS">COTS</option>
                </select>
              </div>

              {/* Lifecycle Status */}
              <div>
                <FieldLabel
                  htmlFor="lifecycleStatus"
                  description={productFieldDescriptions.lifecycleStatus}
                >
                  Lifecycle Status
                </FieldLabel>
                <select
                  id="lifecycleStatus"
                  name="lifecycleStatus"
                  value={formData.lifecycleStatus || ""}
                  onChange={handleInputChange}
                  className="input"
                >
                  <option value="">Select lifecycle status</option>
                  <option value="Planned">Planned</option>
                  <option value="In Development">In Development</option>
                  <option value="Released">Released</option>
                  <option value="Retired">Retired</option>
                  <option value="Obsolete">Obsolete</option>
                </select>
              </div>

              {tagDefinitions
                ?.flatMap((category) => category.keys)
                .map((tagKey) => (
                  <div key={tagKey.id}>
                    <FieldLabel
                      htmlFor={`tag-${tagKey.id}`}
                      description={tagKey.description}
                    >
                      {tagKey.label}
                    </FieldLabel>
                    <TagField
                      id={`tag-${tagKey.id}`}
                      tagKey={tagKey}
                      selectedValues={selectedTags
                        .filter((t) => t.key === tagKey.id)
                        .map((t) => t.value)}
                      onChange={(values) => handleTagChange(tagKey.id, values)}
                    />
                  </div>
                ))}
            </div>
          </div>

          {/* Links Section */}
          {relationshipTypes && relationshipTypes.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  Links
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Link this product to other products.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4">
                {relationshipTypes.map((typeDef) => {
                  const outgoingEntries: RelationshipRowEntry[] = relationships
                    .filter((r) => r.type === typeDef.id)
                    .map((r, i) => ({
                      key: `out:${r.targetProductId}:${i}`,
                      otherProductId: r.targetProductId,
                      otherProductName: r._targetName || "",
                      isOutgoing: true,
                      criticality: r.criticality,
                      since: r.since,
                      until: r.until,
                      notes: r.notes,
                    }));

                  return (
                    <RelationshipPropertyRow
                      key={typeDef.id}
                      typeDef={typeDef}
                      entries={outgoingEntries}
                      currentProductId=""
                      isEditing={true}
                      canEdit={true}
                      onOutgoingChange={(next) => {
                        const nextForType = next.map((e) => ({
                          targetProductId: e.otherProductId,
                          type: typeDef.id,
                          criticality: e.criticality,
                          since: e.since,
                          until: e.until,
                          notes: e.notes,
                          _targetName: e.otherProductName,
                        }));
                        setRelationships((prev) => [
                          ...prev.filter((r) => r.type !== typeDef.id),
                          ...nextForType,
                        ]);
                      }}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Responsibilities Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Responsibilities
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Product owner */}
              <div>
                <FieldLabel
                  htmlFor="productResponsibleId"
                  description={productFieldDescriptions.productResponsibleId}
                >
                  Product owner
                </FieldLabel>
                <TypeaheadUserInput
                  id="productResponsibleId"
                  name="productResponsibleId"
                  value={formData.productResponsibleId || ""}
                  onChange={handleProductResponsibleChange}
                  users={usersResponse || []}
                  loading={loadingUsers}
                  placeholder="Enter email or select user"
                />
              </div>

              {/* Business Accountable */}
              <div>
                <FieldLabel
                  htmlFor="businessAccountableId"
                  description={productFieldDescriptions.businessAccountableId}
                >
                  Business Accountable
                </FieldLabel>
                <TypeaheadUserInput
                  id="businessAccountableId"
                  name="businessAccountableId"
                  value={formData.businessAccountableId || ""}
                  onChange={handleBusinessAccountableChange}
                  users={usersResponse || []}
                  loading={loadingUsers}
                  placeholder="Enter email or select user"
                />
              </div>

              {/* SDL Responsible */}
              <div>
                <FieldLabel
                  htmlFor="sdlResponsibleId"
                  description={productFieldDescriptions.sdlResponsibleId}
                >
                  SDL Responsible
                </FieldLabel>
                <TypeaheadUserInput
                  id="sdlResponsibleId"
                  name="sdlResponsibleId"
                  value={formData.sdlResponsibleId || ""}
                  onChange={handleSdlResponsibleChange}
                  users={usersResponse || []}
                  loading={loadingUsers}
                  placeholder="Enter email or select user"
                />
              </div>
            </div>
          </div>

          {/* Classification Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Classification
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* CRA Classification */}
              <div>
                <FieldLabel
                  htmlFor="craClassification"
                  description={productFieldDescriptions.craClassification}
                >
                  CRA Classification
                </FieldLabel>
                <select
                  id="craClassification"
                  name="craClassification"
                  value={formData.craClassification || ""}
                  onChange={handleInputChange}
                  className="input"
                >
                  <option value="">Select CRA classification</option>
                  {CRA_CLASSIFICATION_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {/* Key Product */}
              <div>
                <FieldLabel
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3"
                  description={productFieldDescriptions.keyProduct}
                >
                  Key Product
                </FieldLabel>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="keyProduct"
                    name="keyProduct"
                    checked={formData.keyProduct || false}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
                  />
                  <label
                    htmlFor="keyProduct"
                    className="ml-2 text-sm text-gray-900 dark:text-gray-100"
                  >
                    Mark as key product
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => navigate("/products")}
              className="btn btn-outline"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createProductMutation.isLoading}
              className="btn btn-primary"
            >
              {createProductMutation.isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Create Product
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
